<template>
   <!--Page alert + loader-->
   <PageStatus :alert="defaultAlert" :table-data="tableData" />

   
      <!--Import Data-->
   <ImportModal
      :form-import-data="formImportData"
      :alert="defaultAlert"
      ask-source-system
      v-model:source-system="formImportData.source_system"
      @file-change="onFileImportChange"
      @import="importRecords"
      @download-template="downloadImportTemplate"
   >
      <!--Import Notes-->
      <div id="accordion">
         <div class="card">
            <div class="card-header">
               <a class="btn" data-bs-toggle="collapse" href="#collapseOne">
                  Import Process
               </a>
            </div>
            <div id="collapseOne" class="collapse show" data-bs-parent="#accordion">
               <div class="card-body">
                  <ol>
                     <li>Click <b>Download Import Template</b>. It has the right columns and two sample rows. Columns marked <b>*</b> (red) are required.</li>
                     <li>Delete the sample rows and paste your users, one per row. Keep row 1 (the column names). The order of the columns does not matter.</li>
                     <li><b>Old system ID</b>: the user's ID in your old system. Users whose Old system ID was already imported are skipped.</li>
                     <li><b>Old system branch ID</b>: the old ID of a branch you imported earlier. Leave it blank to use your current branch (or the Head Office when you are on All Branches).</li>
                     <li><b>Email, Username and Phone</b> must not belong to another user (deleted users count too).</li>
                     <li><b>Role</b>: the name of one of your roles, e.g. Cashier. Leave it blank to assign it later. System Administrator and Developer cannot be given by import.</li>
                     <li><b>Gender</b>: male or female.</li>
                     <li>No password is imported: users set their own with <b>Forgot password</b> on the login page.</li>
                     <li>Enter the <b>Old system name</b> (e.g. quickbooks). Use the same name for every file from that system.</li>
                     <li>Users are always imported into the company you are logged into. For big imports with undo, use <b>Data Migration</b>.</li>
                  </ol>
               </div>
            </div>
         </div>
      </div>
      <!--End Import Notes-->
   </ImportModal>
   <!--Import Data-->

         <!--Filter Data Modal (shared: FilterModal.vue)-->
   <FilterModal
      :draft="filterDraft"
      v-model:search-by="tableData.searchBy"
      :view-options="viewOptions"
      :search-by-options="searchByOptions"
      :alert="defaultAlert"
      @search-by-change="changePlaceholder()"
      @apply="applyFilters"
      @clear="clearFilters"
   />
   <!--End Filter Data Modal-->


<!--Form Modal-->
<RecordFormModal
   :title="isEditing ? 'Update User' : 'Add User'"
   title-icon="bi bi-person"
   :is-editing="isEditing"
   :is-saving="isSaving"
   :alert="defaultAlert"
   @save="saveOrEditRecord"
   @sample="addSampleData"
   @reset="resetForm"
>
   <!--Below id data id. User id is accessed directly in computed properties inside formData_fields function-->
   <HiddenField v-model="formInputs.id" />

   <!-- Row 1: First & Middle Name -->
   <div class="row g-3 mb-0">
      <FormField
         v-model="formInputs.first_name"
         col="col-md-6"
         label="First Name"
         icon="fa fa-user"
         placeholder="e.g. Grace"
         tooltip="Please enter the first name"
         required
         @input="handleInputChange"
         @change="handleInputChange"
      />
      <FormField
         v-model="formInputs.middle_name"
         col="col-md-6"
         label="Middle Name"
         icon="fa fa-user"
         placeholder="e.g. Wanjiru"
         tooltip="Please enter the middle name"
         required
         @input="handleInputChange"
         @change="handleInputChange"
      />
   </div>

   <!-- Row 2: Last Name & Phone -->
   <div class="row g-3 mb-0">
      <FormField
         v-model="formInputs.last_name"
         col="col-md-6"
         label="Last Name"
         icon="fa fa-user"
         placeholder="e.g. Mwangi"
         tooltip="Please enter the last name"
         required
         @input="handleInputChange"
         @change="handleInputChange"
      />
      <FormField
         v-model="formInputs.phonenumber"
         col="col-md-6"
         type="tel"
         inputmode="numeric"
         label="Phone Number"
         icon="fa fa-phone"
         placeholder="e.g. 0712345678"
         tooltip="Enter the phone number"
         required
         @input="handleInputChange"
         @change="handleInputChange"
      />
   </div>

   <!-- Row 3: Email & Gender -->
   <div class="row g-3 mb-0">
      <FormField
         v-model="formInputs.email"
         col="col-md-6"
         type="email"
         label="Email"
         icon="fa fa-envelope"
         placeholder="user@example.com"
         tooltip="Enter the email (used to log in)"
         required
         @input="handleInputChange"
         @change="handleInputChange"
      />
      <SelectField
         v-model="formInputs.gender"
         col="col-md-6"
         name="gender"
         label="Gender"
         icon="fa fa-venus-mars"
         :options="sexOptions"
         :reduce="option => option.value"
         placeholder="Select Gender"
         tooltip="Please select gender."
         :submitted="submitted"
         required
         @change="$clearValidationAlert('#recordForm')"
      />
   </div>

   <!-- Row 4: Date of Birth & Role -->
   <div class="row g-3 mb-0">
      <DateField
         v-model="formInputs.date_of_birth"
         col="col-md-6"
         label="Date of birth"
         icon="fa fa-birthday-cake"
         max="today"
         tooltip="Please choose the date of birth"
         required
         @input="handleInputChange"
         @change="handleInputChange"
      />
      <SelectField
         v-model="formInputs.role"
         col="col-md-6"
         name="role"
         label="Role"
         icon="fa fa-user-secret"
         :options="filteredRolesOptions"
         :reduce="option => option.id"
         option-label="role"
         placeholder="Select role"
         tooltip="Please select role."
         :disabled="isRoleRestricted"
         :submitted="submitted"
         required
         @change="$clearValidationAlert('#recordForm')"
      />
   </div>

   <!-- Row 5: Username & Status -->
   <div class="row g-3 mb-0">
      <FormField
         v-model="formInputs.username"
         col="col-md-6"
         label="Username"
         icon="fa fa-id-badge"
         placeholder="e.g. gmwangi"
         tooltip="Please enter a username"
         required
         @input="handleInputChange"
         @change="handleInputChange"
      />
      <SelectField
         v-model="formInputs.is_active"
         col="col-md-6"
         name="is_active"
         label="Status"
         icon="fa fa-toggle-on"
         :options="activeOptions"
         :reduce="option => option.value"
         placeholder="Select..."
         tooltip="Please enable or disable."
         :disabled="isStatusRestricted"
         :submitted="submitted"
         required
         @change="$clearValidationAlert('#recordForm')"
      />
   </div>

   <!-- Row 6: Password & Confirm -->
   <div class="row g-3 mb-0">
      <FormField
         v-model="formInputs.password"
         col="col-md-6"
         name="password"
         icon="fa fa-lock"
         :label="formInputs.id ? 'Password (Optional)' : 'Password'"
         tooltip="Please enter a password"
         :class="{ 'masked-password': !showPassword }"
         autocomplete="off"
         spellcheck="false"
         autocapitalize="off"
         :required="!formInputs.id"
         @input="handleInputChange"
         @change="handleInputChange"
      />
      <FormField
         v-model="formInputs.confirm_password"
         col="col-md-6"
         icon="fa fa-lock"
         data-match="password"
         :label="formInputs.id ? 'Confirm Password (Optional)' : 'Confirm Password'"
         tooltip="Must be the same as the password"
         :class="{ 'masked-password': !showPassword }"
         autocomplete="off"
         spellcheck="false"
         autocapitalize="off"
         :required="!formInputs.id"
         @input="handleInputChange"
         @change="handleInputChange"
      />
   </div>
   <small v-if="formInputs.id" class="text-muted sa-core-color">
      Leave both fields blank to keep the current password.
   </small>
   <div class="form-check mt-1">
      <input v-model="showPassword" class="form-check-input" type="checkbox" id="showPasswordCheck">
      <label class="form-check-label sa-core-color" for="showPasswordCheck">Show password</label>
   </div>

   <!-- Row 7: Photo (setting formInputs.file to null empties the box) -->
   <div class="row g-3 mb-0">
      <FormField
         v-model="formInputs.file"
         col="col-md-12"
         type="file"
         label="Photo"
         icon="fa fa-image"
         accept="image/*"
         tooltip="Optional: a photo of the user"
         @change="handleInputChange"
      />
   </div>
</RecordFormModal>
<!--End Form Modal-->


<h4 class="sa-core-color fw-semibold mb-2 d-flex align-items-center flex-wrap gap-2">
   <span><i :class="[pageIcon, 'me-2 opacity-75']"></i>{{ pageTitle }}</span>
   <span class="badge rounded-pill sa-theme-pill border fs-6 fw-normal" title="Users logged in right now">
      <i class="fa fa-circle text-success me-1"></i>{{ tableData.usersOnline.online }} of {{ tableData.usersOnline.total }} users online
   </span>
</h4>
   
<!--Tables-->
<div class="card glass mt-0 bg-sa-card">
    <div class="card-body bg-sa-card-body p-0">

      <!-- Toggleable Tabs -->
        <ul class="nav nav-tabs app-tabs" role="tablist">
            <li class="nav-item" role="presentation">
                <router-link
                    to="/user-management/users/"
                    class="nav-link"
                    :class="{ 'app-tab-active': activeTab === 'users' }"
                    @click="activeTab = 'users'"
                >
                    <span class="sa-core-color">Users</span>
                </router-link>
            </li>

            <li class="nav-item" role="presentation">
                <router-link
                    to="/user-management/user_company_branch_access/"
                    class="nav-link"
                    :class="{ 'app-tab-active': activeTab === 'assign-companies-and-branches' }"
                    @click="activeTab = 'assign-companies-and-branches'"
                >
                    <span class="sa-core-color">Company & Branch Access</span>
                </router-link>
            </li>


        </ul>
        <!-- End Toggleable Tabs -->

                <!-- Table action row (shared: TableToolbar.vue) -->
        <TableToolbar
            can-add
            :filter-count="activeFilterCount"
            :columns="tableColumns"
            v-model:hidden-columns="hiddenColumns"
            :selected-count="tableData.selectedRecordIds.length"
            :deleted-view="tableData.recordFilter === 'deletedone'"
            :can-delete="$hasPermission('users.delete')"
            @add="clearForm()"
            @filter="openFilters"
            @clear-selection="clearSelection"
            @bulk-action="runBulkAction"
        >
            <!-- More menu -->
            <template #more>
                <li><h6 class="dropdown-header">Data</h6></li>
                <li>
                    <button type="button" class="dropdown-item" data-bs-toggle="modal" data-bs-target="#importModal">
                        <i class="fa fa-upload fa-fw me-1"></i> Import
                    </button>
                </li>
                <li>
                    <button type="button" class="dropdown-item" @click="exportRecords('xls')">
                        <i class="fa fa-file-excel-o fa-fw me-1 text-success"></i> Export to Excel
                    </button>
                </li>
                <li>
                    <button type="button" class="dropdown-item" @click="exportRecords('csv')">
                        <i class="fa fa-file-text-o fa-fw me-1 text-success"></i> Export to CSV
                    </button>
                </li>
                <li>
                    <button type="button" class="dropdown-item" @click="exportRecords('pdf')">
                        <i class="fa fa-file-pdf-o fa-fw me-1 text-danger"></i> Export to PDF
                    </button>
                </li>
            </template>
        </TableToolbar>
        <!-- Table Search Panel -->
        
        <!-- Table Search Panel -->
        <SearchBox
            v-model:search-query="tableData.searchQuery"
            v-model:records-per-page="tableData.recordsPerPage"
            :placeholder="tableData.searchPlaceHolderText"
            @search-input="onSearchInput"
            @search-click="onSearchButtonClick"
            @per-page-change="onRecordsPerPageChange"
        />
        <!-- What the table is currently showing -->
        <ActiveFilters
            :chips="activeFilterChips"
            @remove="removeFilterChip"
            @clear="clearAllFilterChips"
        />
        <!-- Table -->
        <div class="table-responsive">
            
            <table
    class="table table-hover align-middle mb-0"
    id="bootstrapTable"
    tabindex="0"
    @keydown.up="$moveRowSelection(-1, $event)"
    @keydown.down="$moveRowSelection(1, $event)"
    @keydown.enter="$onTableEnter($event)"
>
      <thead>
      <tr>
         <th>
            <input type="checkbox" v-model="tableData.selectAll" @change="toggleSelectAll" />
         </th>
         <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
         <th class="button hide-sm-td">No</th>
         <SortableTh v-if="isColumnVisible('first_name')" column="first_name" :table-data="tableData" @sort="orderBy">User</SortableTh>
         <SortableTh v-if="isColumnVisible('email')" column="email" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Email</SortableTh>
         <SortableTh v-if="isColumnVisible('phone')" column="phone" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Phone</SortableTh>
         <SortableTh v-if="isColumnVisible('role')" column="role" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Role</SortableTh>
         <SortableTh v-if="isColumnVisible('is_active')" column="is_active" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Status</SortableTh>
         <SortableTh v-if="isColumnVisible('is_loggedIn')" column="is_loggedIn" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Online</SortableTh>
         <th class="button">Action</th>
      </tr>
   </thead>

   <tbody>
      <template v-for="(record, index) in tableData.records" :key="record.id">

         <!-- Main row -->
         <tr class="clickable-row"
             :data-record-id="record.id"
             :class="[
                { 'active-row main-row': tableData.selectedRecordId === record.id, 'row-just-saved': isJustSaved(record) },
                record.pinned_role ? 'pinned-row pinned-' + record.pinned_role : ''
             ]"
             @click="$onRowClick(record, $event)"
             @dblclick="$onRowDblClick(record, $event)"
         >
            <td>
               <input type="checkbox" v-model="tableData.selectedRecordIds" :value="record.id" />
            </td>
            <td class="expand-cell hide-sm-td"
                :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                @click="$toggleDetails(record.id)">
               <i class="fa fa-lg expand-icon ms-1"
                  :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                  :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
            </td>
            <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
            <td v-if="isColumnVisible('first_name')">
               <!-- Phones: + / − and the number go in front of the user (their columns are hidden) -->
               <span
                  class="show-sm-inline expand-cell me-2"
                  :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                  @click="$toggleDetails(record.id)"
               >
                  <i class="fa fa-lg expand-icon"
                     :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle sa-core-color'"
                     :style="expandedRows[record.id] ? null : { color: 'inherit' }"></i>
               </span>
               <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span>
               <img v-if="record.file_path" :src="`${$baseUrl}/users/${record.file_path}`" height="24" width="24" class="rounded-circle me-1 object-fit-cover" />
               {{ getFullName(record) }}
               <span v-if="record.pinned_role === 'system_admin'" class="badge bg-warning text-dark ms-1">
                  <i class="fa fa-shield me-1"></i>System Admin
               </span>
               <span v-else-if="record.pinned_role === 'developer'" class="badge bg-info text-dark ms-1">
                  <i class="fa fa-code me-1"></i>Developer
               </span>
            </td>
            <td v-if="isColumnVisible('email')" class="hide-sm-td">{{ record.email }}</td>
            <td v-if="isColumnVisible('phone')" class="hide-sm-td">{{ record.phone }}</td>
            <td v-if="isColumnVisible('role')" class="hide-sm-td">{{ getRoleLabel(record.role) }}</td>
            <td v-if="isColumnVisible('is_active')" class="hide-sm-td">
               <span :class="Number(record.is_active) === 1 ? 'badge bg-success' : 'badge bg-secondary'">
                  {{ Number(record.is_active) === 1 ? 'Active' : 'Inactive' }}
               </span>
            </td>
            <td v-if="isColumnVisible('is_loggedIn')" class="hide-sm-td text-nowrap" :title="record.last_login_at ? 'Last login: ' + $ConvertToLocalDate(record.last_login_at) : 'Never logged in'">
               <i class="fa fa-circle me-1" :class="Number(record.is_loggedIn) === 1 ? 'online-dot' : 'text-secondary'"></i>{{ Number(record.is_loggedIn) === 1 ? 'Online' : 'Offline' }}
            </td>
            <td>
               <RowActions
                  :record="record"
                  :modal-id="'deleteCrudModal' + record.id"
                  :can-soft-delete="$hasPermission('users.delete') && !record.pinned_role"
                  :can-restore="$hasPermission('users.delete')"
                  :can-delete-permanently="$hasPermission('users.delete') && !record.pinned_role"
                  @edit="editRecord"
                  @delete-type="setDeleteType"
               />

               <!--Delete Modal-->
               <DeleteModal
                  :modal-id="'deleteCrudModal' + record.id"
                  :title="deleteModalTitle"
                  :message="deleteModalMessage"
                  @confirm="deleteRecord(record.id)"
               />
               <!--End Delete Modal-->
            </td>
         </tr>

         <!-- Hidden expanded row -->
         <tr class="collapse-row">
            <td class="p-0"></td>
            <td class="p-0 hide-sm-td"></td>
            <td colspan="8" class="p-0">
               <div :id="'tableDetails' + record.id" class="collapse table-collapse table-details">
                  <div class="p-3 details-list">
                     <div><i class="fa fa-hashtag"></i><strong>User Number:</strong> {{ record.user_number }}</div>
                     <div><i class="fa fa-id-badge"></i><strong>Username:</strong> {{ record.username }}</div>
                     <div><i class="fa fa-building"></i><strong>Home Company:</strong> {{ record.company_name }}</div>
                     <div><i class="fa fa-sitemap"></i><strong>Home Branch:</strong> {{ record.branch_name || 'All Branches' }}</div>
                     <div>
                        <i class="fa fa-key"></i><strong>Access:</strong>
                        <span :class="record.access_branch_count ? 'badge bg-info text-dark' : 'badge bg-warning text-dark'">
                           {{ record.access_branch_count || 0 }} branches · {{ record.access_company_count || 0 }} companies
                        </span>
                     </div>

                     <!-- Shown on phones, or always when the column is hidden (Columns button) -->
                     <div :class="{ 'show-sm-td': isColumnVisible('first_name') }"><i class="fa fa-user"></i><strong>User:</strong> {{ getFullName(record) }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('email') }"><i class="fa fa-envelope"></i><strong>Email:</strong> {{ record.email }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('phone') }"><i class="fa fa-phone"></i><strong>Phone:</strong> {{ record.phone }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('role') }"><i class="fa fa-users"></i><strong>Role:</strong> {{ getRoleLabel(record.role) }}</div>
                     <div :class="{ 'show-sm-td': isColumnVisible('is_active') }">
                        <i class="fa fa-toggle-on"></i><strong>Status:</strong>
                        <span :class="Number(record.is_active) === 1 ? 'badge bg-success' : 'badge bg-secondary'">
                           {{ Number(record.is_active) === 1 ? 'Active' : 'Inactive' }}
                        </span>
                     </div>

                     <!-- Always in details -->
                     <div><i class="fa fa-venus-mars"></i><strong>Gender:</strong> {{ getGenderLabel(record.gender) }}</div>
                     <div><i class="fa fa-birthday-cake"></i><strong>Date of Birth:</strong> {{ record.date_of_birth }}</div>
                     <div v-if="$hasPermission('view-location')"><i class="fa fa-location-arrow"></i><strong>Created From:</strong> {{ record.current_location }}</div>
                     <div><i class="fa fa-user"></i><strong>Created By:</strong> {{ [record.user_first_name, record.user_middle_name].filter(Boolean).join(' ') }}</div>
                     <div><i class="fa fa-edit"></i><strong>Updated By:</strong> {{ record.updatedby_firstname }}</div>
                     <div><i class="fa fa-calendar"></i><strong>Date Created:</strong> {{ $ConvertToLocalDate(record.date_created) }}</div>
                     <div><i class="fa fa-calendar"></i><strong>Date Updated:</strong> {{ $ConvertToLocalDate(record.date_updated) }}</div>
                     <div><i class="fa fa-circle" :class="Number(record.is_loggedIn) === 1 ? 'text-success' : 'text-secondary'"></i><strong>Logged In:</strong>
                        {{ Number(record.is_loggedIn) === 1 ? 'Yes (online)' : 'No' }}
                        <span v-if="record.last_login_at" class="text-muted sa-core-color">· last login {{ $ConvertToLocalDate(record.last_login_at) }}</span>
                     </div>
                     <div v-if="$hasPermission('view-ip-address')"><i class="fa fa-desktop"></i><strong>IP Address:</strong> {{ record.ip_address }}</div>
                  </div>
               </div>
            </td>
         </tr>

      </template>

      <!-- No rows: blank while loading, then the empty message in the middle -->
      <tr v-if="tableData.records.length === 0" class="table-filler">
         <td colspan="10" class="text-center align-middle" :style="{ height: fullPageHeight() }">
            <TableEmptyState
               v-if="!tableLoading"
               :has-filters="activeFilterCount > 0 || !!tableData.appliedSearchQuery"
               :can-add="true"
               :title="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'No users yet'"
               :message="activeFilterCount > 0 || tableData.appliedSearchQuery ? '' : 'Click Add to create the first user.'"
               icon="fa fa-users"
               @add="clearForm()"
               @clear="clearAllFilterChips"
            />
         </td>
      </tr>
   </tbody>

   <tfoot class="table-light">
      <tr>
         <td colspan="10">
            <!-- Pagination (hidden when there are no rows; its space is kept) -->
            <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
            <Pagination
               v-else
               :currentPage="tableData.currentPage"
               :totalPages="tableData.totalPages"
               :hasNextPage="tableData.hasNextPage"
               :totalRecords="tableData.totalRecords"
               :startRecord="tableData.startRecord"
               :endRecord="tableData.endRecord"
               :showFullPagination="tableData.totalRecords > 0"
               :lastKnownPage="lastKnownPage"
               @page-changed="fetchRecords"
               @loaderMessage="updateLoaderMessage"
               @last-page-request="goToLastPage"
            />
         </td>
      </tr>
   </tfoot>
</table>
        </div>
    </div>
</div>
<!--End Tables-->



</template>
<script>
import vSelect from "vue-select"
import "vue-select/dist/vue-select.css"

import PageStatus from "../../../components/admin/PageStatus.vue";
import TableToolbar from "../../../components/admin/TableToolbar.vue";
import FilterModal from "../../../components/admin/FilterModal.vue";
import ActiveFilters from "../../../components/admin/ActiveFilters.vue";
import Pagination from "../../../components/admin/Pagination.vue";
import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
import SortableTh from "../../../components/admin/SortableTh.vue";
import DeleteModal from "../../../components/admin/DeleteModal.vue";
import RowActions from "../../../components/admin/RowActions.vue";
import SearchBox from "../../../components/admin/SearchBox.vue";
import ImportModal from "../../../components/admin/ImportModal.vue";
import RecordFormModal from "../../../components/admin/RecordFormModal.vue";
import FormField from "../../../components/admin/FormField.vue";
import SelectField from "../../../components/admin/SelectField.vue";
import HiddenField from "../../../components/admin/HiddenField.vue";
import DateField from "../../../components/admin/DateField.vue";
import tableMixin from "../../../mixins/tableMixin.js";
import usersMixin from "./mixins/usersMixin.js";
import axios from 'axios';


export default {
   name: 'Users',
   mixins: [tableMixin, usersMixin], // shared table methods first, then Users' own
   components: {
      vSelect,
      PageStatus,
      TableToolbar,
      FilterModal,
      ActiveFilters,
      Pagination,
      TableEmptyState,
      SortableTh,
      DeleteModal,
      RowActions,
      SearchBox,
      ImportModal,
      RecordFormModal,
      FormField,
      SelectField,
      HiddenField,
      DateField,
   },
   data() {
      return {
         // Standard data (isSaving, tableData, defaultAlert, formImportData ...) comes from tableMixin
         standardPageSetup: true,          // tableMixin sets up Enter-to-save and table shortcuts
         apiUrl: "/users/api_users.php",   // used by tableMixin's fetchRecords, delete, import, export
         importTemplateFileName: "users_import_template.xlsx",
         exportFileName: "usersExport",    // name of the exported file
         columnsKey: "users",              // saves this page's hidden columns (Columns button)
         activeTab: 'users',
         showPassword: false,
         formInputs: this.getDefaultFormInputs(),
         rolesOptions: [],
         currentUserRole: '',
         pageIcon: 'fa fa-users', // same icon as this page in the sidebar
         // Filter window: View
         viewOptions: [
            { label: "All Records", value: "all" },
            { label: "Records By Me", value: "byme" },
            { label: "Records Deleted", value: "deletedone" },
         ],
         // Filter window: Search By
         searchByOptions: [
            { label: "First Name", value: "first_name" },
            { label: "Middle Name", value: "middle_name" },
            { label: "Last Name", value: "last_name" },
            { label: "Email", value: "email" },
            { label: "Phone", value: "phone" },
            { label: "Username", value: "username" },
         ],
         sexOptions: [
            { label: "Male", value: 1 },
            { label: "Female", value: 0 }
         ],
         activeOptions: [
           { label: "Active", value: 1 },
           { label: "Inactive", value: 0 }
         ]
      };
   },
   mounted(){
      this.fetchRolesOptions(); // roles for the form and the table
   },
   async created() {      
      //alert(this.$store.state.isLoggedIn);
      //this.retriveUserDetails();           
      await this.fetchRecords(
                this.tableData.currentPage,
                this.tableData.currentSortColumn,
                "DESC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.loaderMessageDefault,
            );
      this.$checkIfCanFetchLocation();//Only applies if location is enabled for user. If not Log user out for him to enable location 
   },
   computed: {
    // Page heading that follows the chosen filter.
      pageTitle() {
          const titles = {
              all: "Users",
              byme: "Users By Me",
              deletedone: "Users Deleted Records",
          };
          return titles[this.tableData.recordFilter] || "Users";
      },
      // Columns the user can show or hide (Columns button)
      tableColumns() {
          return [
              { key: "first_name", label: "User" },
              { key: "email", label: "Email" },
              { key: "phone", label: "Phone" },
              { key: "role", label: "Role" },
              { key: "is_active", label: "Status" },
              { key: "is_loggedIn", label: "Online" },
          ];
      },
      isRoleRestricted() {
       const restrictedRoles = [1, 2];//IDs of roles from from roles table
       return restrictedRoles.includes(Number(this.currentUserRole)); // Replace with your actual role variable
      },
      isStatusRestricted() {
       return !!this.formInputs.id &&
           [1, 2].includes(Number(this.formInputs.role));
      },
      filteredRolesOptions() {
          // When adding a new user, hide roles 1 and 2
          if (!this.formInputs.id) {
              return this.rolesOptions.filter(option => option.id !== 1 && option.id !== 2);
          }

          // When editing, allow the user's existing role to remain visible
          return this.rolesOptions.filter(option => {
              return option.id !== 1 &&
                     option.id !== 2 ||
                     option.id === Number(this.formInputs.role);
          });
      }
   },
   watch: {
     currentUserRole(newVal) {
       if (newVal === 1 || newVal === 2) {
           this.formInputs.role = newVal;
           this.$nextTick(() => {
             this.$clearValidationStates('#recordForm');
           });
         }
     },
   },
   methods: {
      addSampleData() {
         const pick = (list) => list[Math.floor(Math.random() * list.length)];

         // One random value so phone, email and username are unique
         const suffix = String(Math.floor(Math.random() * 100000000)).padStart(8, "0");

         // Random gender, with a first name that matches it
         const gender = Math.random() < 0.5 ? 1 : 0; // 1 = Male, 0 = Female (matches sexOptions)
         const maleNames = ["John", "Brian", "Kevin", "Peter", "David", "James", "Samuel", "Daniel", "Joseph", "Collins"];
         const femaleNames = ["Mary", "Faith", "Grace", "Mercy", "Joy", "Esther", "Ann", "Lucy", "Sharon", "Cynthia"];
         const middleNames = ["Wanjiru", "Otieno", "Kiprono", "Achieng", "Mutua", "Njeri", "Chebet", "Wafula", "Akinyi", "Kamau"];
         const lastNames = ["Mwangi", "Ochieng", "Kipchoge", "Odhiambo", "Kariuki", "Mutiso", "Wekesa", "Korir", "Njoroge", "Omondi"];

         const firstName = pick(gender === 1 ? maleNames : femaleNames);
         const middleName = pick(middleNames);
         const lastName = pick(lastNames);

         // Random date of birth: age 18–60, day 1–28 so it's valid in every month
         const year = new Date().getFullYear() - (18 + Math.floor(Math.random() * 43));
         const month = String(1 + Math.floor(Math.random() * 12)).padStart(2, "0");
         const day = String(1 + Math.floor(Math.random() * 28)).padStart(2, "0");

         this.formInputs = {
            ...this.getDefaultFormInputs(),
            first_name: firstName,
            middle_name: middleName,
            last_name: lastName,
            phonenumber: "07" + suffix,
            email: `${firstName}.${lastName}${suffix}@example.com`.toLowerCase(),
            username: `${firstName}${lastName}${suffix}`.toLowerCase(),
            gender: gender,
            role: this.filteredRolesOptions.length ? this.filteredRolesOptions[0].id : "",
            is_active: 1,              // Active
            date_of_birth: `${year}-${month}-${day}`,
            password: "Sample@1234",
            confirm_password: "Sample@1234",
         };

         this.showPassword = true; // so you can see the sample password
         this.submitted = false;
         this.$clearValidationStates("#recordForm");
         this.$scrollToTopModal("recordFormModal");
      },
      fetchRolesOptions(){
         let url = `${this.apiUrl}?action=fetchRoles`;

         axios.get(url,this.$handleConfig())
            .then((response) => {
               if (response.data.status=="success") {
                  this.handleFetchRolesSuccess(response);
               } else {
                  this.handleAlert("starting",response.data.message,"danger","page");
               }
            })
            .catch((error) => {
               if (error.message === 'Network Error') {
                  this.handleAlert("ending","No internet connection. Please check your network.","danger","page");
                } else {
                  this.handleAlert("ending","Something went wrong. Please try again later.","danger","page");
                }
               
            });
      },
      // Selection bar: System Admin and Developer users (pinned_role) are never deleted,
      // same as the row menu. Take them out of the ticked rows, then run the shared bulk action.
      runBulkAction(deleteType = "soft") {
         if (deleteType === "soft") {
            const protectedIds = this.tableData.records
               .filter((record) => record.pinned_role)
               .map((record) => String(record.id));
            const kept = this.tableData.selectedRecordIds.filter((id) => !protectedIds.includes(String(id)));
            if (!kept.length) {
               this.handleAlert("ending", "System Admin and Developer users can't be deleted.", "warning", "page");
               return;
            }
            this.tableData.selectedRecordIds = kept;
         }
         return tableMixin.methods.runBulkAction.call(this, deleteType);
      },
      getDefaultFormInputs(){
         return {
            //Form Data
            id: "",
            first_name: "",
            middle_name: "",
            last_name: "",
            phonenumber: "",
            gender: "",
            role: "",
            date_of_birth: "",
            email: "",
            username: "",
            //mfa_enabled: "",
            //is_locationRequired: "",
            is_active: "",
            password: "",
            confirm_password: "",
            crudImage: "",
            select_drop_down: [],
            file: null,
         };
      },
      getDefaultFormImportData(){
         return {
            dataFile: null,
            source_system: "",      // old system name, e.g. quickbooks
            templateLoading: false, // spinner on the Download Import Template button
            importErrors: [],       // rows that were not imported, and why
            uploading: false,
            progress: 0,
            message: "",
            messageType: "",
         }
      },
            // Table defaults: the standard ones (tableMixin) + this page's own
      getDefaultTableData(){
         return {
            ...this.baseTableData(),
            searchBy: 'first_name',
            searchPlaceHolderText: 'Search by First Name',
            usersOnline: { online: 0, total: 0 },
         };
      },
      resetForm() {
         const id = this.formInputs.id; // keep the id when editing, so Save still updates

         this.formInputs = {
            ...this.getDefaultFormInputs(),
            id: id,
         };

         this.showPassword = false;
         this.submitted = false;

         // A file input can't be cleared through v-model
         if (this.$refs.fileInput) this.$refs.fileInput.value = "";

         this.$clearValidationStates("#recordForm");
      },
      getGenderLabel(value) {
         const option = this.sexOptions.find(
            option => Number(option.value) === Number(value)
         );

         return option ? option.label : value;
      },
      getFullName(record) {
         return [record.first_name, record.middle_name, record.last_name]
            .filter(Boolean)
            .join(' ');
      },
      getRoleLabel(value) {
         const option = this.rolesOptions.find(
            option => Number(option.id) === Number(value)
         );

         return option ? option.role : value;
      },
      saveOrEditRecord() {   
      if (this.isSaving) return;
      this.isSaving = true;

      try {      
         this.$scrollToTopModal("recordFormModal"); 
         this.submitted = true;

         const formIsValid = this.$validateForm('#recordForm');
         if (!formIsValid) { 
              this.$playErrorSound();
              this.handleAlert("starting", "Please correct the highlighted fields.", "danger", "modal");
              this.isSaving = false;
              return;
         }

         this.handleAlert("starting","Please wait submiting","info","modal");

         let formData = this.formData_fields();

         if (!this.formInputs.id) {
            this.tableData.currentPage = 1;
         }
         
         //To test performance
         const start = performance.now(); // Start time
         // Perform the POST request
         axios.post(this.apiUrl, formData, this.$handleConfig())
            .then((response) => {     
               if (response.data.status =='success') {
                  const end = performance.now(); // End time
                  const duration = end - start;
                  console.log("Time taken to receive:" + duration.toFixed(2));
                  //alert(response.data.results.file_path);
                  // Only refresh the header name/photo when you edited your own account
                  if (response.data.results.is_self) {
                     localStorage.setItem('first_name', response.data.results.firstname);
                     localStorage.setItem('user_pic', response.data.results.file_path);
                     this.$store.commit('setUserDetails', {
                        user: {
                           user_pic: response.data.results.file_path,
                           first_name: response.data.results.firstname
                        }
                     });
                  }

                  this.handleSaveOrEditSuccess(response);
               } else {
                   this.handleError("starting",response.data.message[0],"danger","modal");
               }
            })
            .catch((error) => {
               if (error.message === 'Network Error') {
                  this.handleAlert("ending","No internet connection. Please check your network.","danger","modal");
                } else {
                  this.handleAlert("ending","Something went wrong. Please try again later.","danger","modal");
                }
               //this.handleError("starting",error,"danger","modal");
             })
            .finally(() => {
                this.isSaving = false;
            });} catch (error) {
            this.isSaving = false;
            throw error;
        }
      },
      formData_fields(){
         let formData = new FormData();
         formData.append("id", this.formInputs.id);
         formData.append("first_name", this.formInputs.first_name);
         formData.append("middle_name", this.formInputs.middle_name);
         formData.append("last_name", this.formInputs.last_name);
         formData.append("phonenumber", this.formInputs.phonenumber);
         formData.append("gender", this.formInputs.gender);
         formData.append("latitude", this.$store.state.latitude);
         formData.append("longitude", this.$store.state.longitude);
         formData.append("role", this.formInputs.role);
         formData.append("date_of_birth", this.formInputs.date_of_birth);
         formData.append("email", this.formInputs.email);
         formData.append("username", this.formInputs.username);
         //formData.append("mfa_enabled", this.formInputs.mfa_enabled);
         formData.append("is_active", this.formInputs.is_active);
         //formData.append("is_locationRequired", this.formInputs.is_locationRequired);
         formData.append("password", this.formInputs.password);
         formData.append("confirm_password", this.formInputs.confirm_password);
         formData.append("file", this.formInputs.file); // Make sure this is a valid file object
         formData.append('action', 'saveOrEditRecord');
         return formData;
      },
      editRecord(records) {
         this.showPassword = false;
         this.isEditing = true;         
         this.currentUserRole = records.role ?? '';
         this.formInputs.id = records.id ?? '';
         this.formInputs.first_name = records.first_name ?? '';
         this.formInputs.middle_name = records.middle_name ?? '';
         this.formInputs.last_name = records.last_name ?? '';
         this.formInputs.phonenumber = records.phone ?? '';
         //this.formInputs.gender = records.gender ?? '';
         //$(this.$refs.gender).val(this.formInputs.gender).trigger('change');

         //this.formInputs.role = records.role ?? '';
         this.formInputs.gender = records.gender != null
            ? Number(records.gender)
            : '';

         this.formInputs.role = records.role != null
            ? Number(records.role)
            : '';
         //$(this.$refs.role).val(this.formInputs.role).trigger('change');

         this.formInputs.date_of_birth = records.date_of_birth ? this.$formatDate(records.date_of_birth) : '';
         this.formInputs.email = records.email ?? '';
         this.formInputs.username = records.username ?? '';
         /*this.formInputs.mfa_enabled = records.mfa_enabled ?? '';
         $(this.$refs.mfa_enabled).val(this.formInputs.mfa_enabled).trigger('change');*/

         this.formInputs.is_active = records.is_active ?? '';
         //$(this.$refs.is_active).val(this.formInputs.is_active).trigger('change');

         /*this.formInputs.is_locationRequired = records.is_locationRequired ?? '';
         $(this.$refs.is_locationRequired).val(this.formInputs.is_locationRequired).trigger('change');*/

         //this.formInputs.password = records.password ?? '';
         this.formInputs.password = '';
         this.formInputs.confirm_password = '';
         this.$clearValidationStates('#recordForm');
      },
         },
}; 
</script>